import CoverPhotoFallback from 'src/shared/assets/images/cover-photo-fallback.jpg'
import { useUserPhoto } from 'src/shared/features/users/api/accounts/use-user-photo'

export { CoverPhotoFallback }

// Constants for file validation
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const MAX_DIMENSION = 2048 // Maximum width/height in pixels

export const validateImageFile = async (file: File): Promise<string | null> => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return 'Image size should be less than 5MB'
  }

  // Check file type
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return 'Please upload a valid image (JPEG, PNG, or WebP)'
  }

  // Check image dimensions
  try {
    const dimensions = await getImageDimensions(file)
    if (dimensions.width > MAX_DIMENSION || dimensions.height > MAX_DIMENSION) {
      return `Image dimensions should not exceed ${MAX_DIMENSION}x${MAX_DIMENSION} pixels`
    }
  } catch (error) {
    console.error(error)
    return 'Unable to validate image dimensions'
  }

  return null
}

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
    }
    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }
    img.src = URL.createObjectURL(file)
  })
}

const CoverPhoto = ({ userId, className }: { userId: number; className?: string }) => {
  let { data: photo } = useUserPhoto('cover_photo', userId)

  if (!photo) {
    photo = CoverPhotoFallback
  }

  const imageStyle = photo
    ? {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
      }
    : undefined

  return (
    <div className={`overflow-hidden ${className}`}>
      <img src={photo} alt="cover photo" style={imageStyle} />
    </div>
  )
}

export default CoverPhoto
